import { createSlice } from "@reduxjs/toolkit";

const initialState = {
  data: null,
  defaultAddress: null,
  myBooks: [],
  booksLoading: true,
  // Set only when a book-list load succeeded: whose list it is and when that load started.
  // `booksLoading === false` alone doesn't mean `myBooks` can be trusted (see selectBooksLoadedAt).
  booksLoaded: null
};

const profileSlice = createSlice({
  name: "profile",
  initialState,
  reducers: {
    setProfile: (state, action) => {
      state.data = action?.payload;
    },
    updateProfile: (state, action) => {
      state.data = { ...state.data, ...action.payload }
    },
    setDefaultAddressSidebar: (state, action) => {
      state.defaultAddress = action.payload;
    },
    setBooksLoading: (state, action) => {
      state.booksLoading = !!action.payload;
    },
    setBooksLoaded: (state, action) => {
      state.booksLoaded = action.payload ?? null;
    },
    setMyBooks: (state, action) => {
      state.myBooks = action.payload ?? []
    },
    addBook: (state, action) => {
      if (!!action.payload) {
        const existingIndex = state.myBooks.findIndex(book => book._id === action.payload._id);
        if (existingIndex >= 0) {
          state.myBooks[existingIndex] = { ...state.myBooks[existingIndex], ...action.payload };
        } else {
          // First, like the API's newest-first list: appended, a new book sat below every
          // older card on My Books until a refresh re-sorted it.
          state.myBooks = [action.payload, ...state.myBooks];
        }
      }
    },
    updateBook: (state, action) => {
      if (!!action.payload._id) {
        state.myBooks = state.myBooks.map((book) => {
          if (book._id !== action.payload._id) return book;
          const merged = { ...book, ...action.payload };
          if (
            action.payload.coverImage != null &&
            action.payload.coverImageThumbnail === undefined
          ) {
            delete merged.coverImageThumbnail;
          }
          return merged;
        });
      }
    },
    removeBook: (state, action) => {
      state.myBooks = state.myBooks.filter((book) => book._id !== action.payload);
    },
    updateBookOrderDate: (state, action) => {
      const { bookId } = action.payload

      state.myBooks = state.myBooks.map(book => book._id !== bookId ? book : ({
        ...book,
        lastOrderCreatedAt: new Date().toISOString()
      }))
    },
    markStepAsVisited: (state, action) => {
      state.myBooks = state.myBooks.map(book => {
        const completedSteps = book?.completedSteps || []
        return book._id !== action.payload.id ? book : {
          ...book,
          completedSteps: [...new Set([...completedSteps, action.payload.currentStepCode])]
        }
      })
    }
  },
});

export const { setProfile, setDefaultAddressSidebar, setMyBooks, addBook, updateBook, removeBook, updateProfile, setBooksLoading, setBooksLoaded, markStepAsVisited, updateBookOrderDate } = profileSlice?.actions;

/**
 * When the signed-in customer's `myBooks` last loaded successfully in this document, or null when
 * this document can't vouch for it: signed out, still loading, the load failed, or the list belongs
 * to a different customer than the current session. Pass it to `pruneSelectedPlanLocalStorage`.
 */
export const selectBooksLoadedAt = (state) => {
  const session = state?.authProvider?.session;
  const { booksLoading, booksLoaded } = state?.profile || {};
  if (session?.status !== "authorized" || session?.user?.role !== "user") return null;
  if (booksLoading || !booksLoaded || !session?.user?._id) return null;
  if (String(booksLoaded.userId) !== String(session.user._id)) return null;
  return booksLoaded.at;
};

export default profileSlice?.reducer;
